import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";

import { CalendarDaysIcon, Cog6ToothIcon } from "@heroicons/react/20/solid";
import { ArrowLeftRightIcon, RocketIcon } from "lucide-react";
import { useSupplierRecord } from "@/app/Suppliers/Hooks/useSupplierRecord";
import { useSupplierActions } from "@/app/Suppliers/Hooks/useSupplierActions";
import { MediaCollection, RelocationStatus } from "@/gql/graphql";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useSupplierImageDropzone } from "@/app/Suppliers/Hooks/useSupplierImageDropzone";
import { SupplierImageDropzone } from "@/app/Suppliers/Components/SupplierImageDropzone";

export function SupplierRecordScreen() {
  const supplier = useSupplierRecord();
  const getActions = useSupplierActions();

  return (
    <RecordScreen
      tabParams={{ id: supplier.id }}
      title={supplier.name}
      image={
        <div className="flex gap-3">
          <SupplierImageUploadButton
            supplierId={supplier.id}
            media={supplier.icon}
            collection={MediaCollection.Icon}
            label="Icon"
            className="size-20"
          />
          <SupplierImageUploadButton
            supplierId={supplier.id}
            media={supplier.logo}
            collection={MediaCollection.Logo}
            label="Logo"
            className="h-20 w-40"
          />
        </div>
      }
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/suppliers"
                params={(prev) => prev}
              >
                Suppliers
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{supplier.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      actionsGroups={getActions(supplier)}
      tabs={[
        {
          name: "Bookings",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id/bookings",
          Icon: CalendarDaysIcon,
        },
        {
          name: "Relocations",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id/relocations",
          search: {
            relocationStatus: [RelocationStatus.Ready, RelocationStatus.Paused],
          },
          Icon: ArrowLeftRightIcon,
        },
        {
          name: "Ads",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id/ads",
          Icon: RocketIcon,
          isAdminTab: true,
        },
        {
          name: "Settings",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
          search: { supplier: supplier.id },
          Icon: Cog6ToothIcon,
        },
      ]}
    />
  );
}

function SupplierImageUploadButton({
  supplierId,
  media,
  collection,
  label,
  className,
}: {
  supplierId: string;
  media: NonNullable<SupplierRecord>["logo"];
  collection: MediaCollection;
  label: string;
  className: string;
}) {
  const dropzone = useSupplierImageDropzone({ supplierId, collection });

  return (
    <SupplierImageDropzone
      dropzone={dropzone}
      media={media}
      label={label}
      className={className}
    />
  );
}
